import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CreateHealthcareProviderExcerptProvider } from "./context/CreateHealthcareProviderExcerptContext";
import { useCreateHealthcareProviderExcerpt } from "./context/CreateHealthcareProviderExcerptHook";
import Container from "@mui/material/Container";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Alert from "@mui/material/Alert";
import Typography from "@mui/material/Typography";
import LoadingButton from "@mui/lab/LoadingButton";
import StickyContent from "src/components/atoms/StickyContent/StickyContent";
import FieldPaper from "src/components/organisms/FieldPaper/FieldPaper";
import SelectHealthcareProvider from "./steps/SelectHealthcareProvider/SelectHealthcareProvider";
import InfoBox from "./InfoBox";
import CreateHealthcareProviderExcerptResultDialog from "@/src/components/organisms/CreateHealthcareProviderExcerptResultDialog/CreateHealthcareProviderExcerptResultDialog";
import {
    GetHealthcareProvidersDocument,
    useIssueCredentialsForHealthcareProvidersMutation,
} from "@/src/graphql/generated/api/graphql";

// ponytail: matches the backend's HEALTHCARE_PROVIDER_HAS_NON_REVOKED_CREDENTIAL reason phrase, switch to a reason code once CredentialMutationFailureDto exposes one
const ALREADY_ISSUED =
    "The healthcare provider has a non revoked issued-credential";

const CreateHealthcareProviderExcerpt = () => {
    const { t } = useTranslation();
    const [openResultsDialog, setOpenResultsDialog] = useState(false);
    const [success, setSuccess] = useState(false);
    const [alreadyIssued, setAlreadyIssued] = useState(false);
    // handleSubmit validates async before calling onValid, so a fast double click reaches it twice
    const submitting = useRef(false);
    const createHealthcareProviderExcerpt =
        useCreateHealthcareProviderExcerpt();
    const { form } = createHealthcareProviderExcerpt;
    const healthcareProvider = form.watch("healthcareProvider");

    const [issueCredentialForHealthcareProvider, { loading }] =
        useIssueCredentialsForHealthcareProvidersMutation({
            refetchQueries: [GetHealthcareProvidersDocument],
        });

    const onSubmit = form.handleSubmit(async (values) => {
        if (submitting.current) return;
        submitting.current = true;

        try {
            const { data } = await issueCredentialForHealthcareProvider({
                variables: { ids: [values.healthcareProvider.id] },
            });
            const results = data?.issueCredentialsForHealthcareProviders ?? [];

            setSuccess(results.some(({ successful }) => successful));
            setAlreadyIssued(
                results.some(({ unsuccessful }) =>
                    unsuccessful?.reasonPhrase.startsWith(ALREADY_ISSUED)
                )
            );
        } catch {
            setSuccess(false);
            setAlreadyIssued(false);
        } finally {
            submitting.current = false;
            setOpenResultsDialog(true);
        }
    });

    const onCloseResultsDialog = () => {
        setOpenResultsDialog(false);
    };

    return (
        <CreateHealthcareProviderExcerptProvider
            value={createHealthcareProviderExcerpt}
        >
            <form onSubmit={onSubmit} data-testid="form">
                <Container>
                    <Stack spacing={3}>
                        <Box>
                            <Typography variant="h4">
                                {t("healthcare-provider-excerpts-create.title")}
                            </Typography>
                        </Box>
                        <Box sx={{ flexGrow: 1 }}>
                            <Grid container spacing={4}>
                                <Grid size={{ xs: 12, md: 4 }}>
                                    <Stack spacing={3}>
                                        <SelectHealthcareProvider />
                                    </Stack>
                                </Grid>
                                <Grid size={{ xs: 12, md: 8 }}>
                                    <StickyContent>
                                        <FieldPaper
                                            title={
                                                <Typography variant="h6">
                                                    {t(
                                                        "healthcare-provider-excerpts-create.general-info"
                                                    )}
                                                </Typography>
                                            }
                                        >
                                            <Box>
                                                <Stack spacing={3}>
                                                    {!healthcareProvider ? (
                                                        <Box>
                                                            <Alert severity="info">
                                                                {t(
                                                                    "healthcare-provider-excerpts-create.fill-in-healthcare-provider"
                                                                )}
                                                            </Alert>
                                                        </Box>
                                                    ) : null}

                                                    <InfoBox />
                                                    <Stack direction="row">
                                                        <Box flex="1 1 auto" />
                                                        <LoadingButton
                                                            role="submit-button"
                                                            loading={loading}
                                                            variant="contained"
                                                            type="submit"
                                                            disabled={
                                                                !form.formState
                                                                    .isValid
                                                            }
                                                        >
                                                            {t(
                                                                "validated-question-create.submit"
                                                            )}
                                                        </LoadingButton>
                                                    </Stack>
                                                </Stack>
                                            </Box>
                                        </FieldPaper>
                                    </StickyContent>
                                </Grid>
                            </Grid>
                        </Box>
                    </Stack>
                </Container>
            </form>
            <CreateHealthcareProviderExcerptResultDialog
                open={openResultsDialog}
                onClose={onCloseResultsDialog}
                success={success}
                alreadyIssued={alreadyIssued}
            />
        </CreateHealthcareProviderExcerptProvider>
    );
};

export default CreateHealthcareProviderExcerpt;
